import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';

import { useToastStore, type Toast, type ToastTone } from './toastStore';

const TONE_STYLES: Record<ToastTone, string> = {
  info: 'bg-[var(--surface-elevated)] text-[var(--text-primary)] border-[var(--border)]',
  success: 'bg-[var(--success-subtle)] text-[var(--success-text)] border-[var(--success)]',
  error: 'bg-[var(--danger-subtle)] text-[var(--danger-text)] border-[var(--danger)]',
  warning: 'bg-[var(--warning-subtle)] text-[var(--warning-text)] border-[var(--warning)]'
};

function ToastItem({ toast }: { toast: Toast }) {
  const { t } = useTranslation();
  const dismiss = useToastStore((state) => state.dismiss);

  useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = setTimeout(() => {
      dismiss(toast.id);
    }, toast.duration);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.duration, dismiss]);

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-center gap-3 rounded-md border px-4 py-3 shadow-md',
        'text-body-sm',
        TONE_STYLES[toast.tone]
      )}
    >
      <span className="flex-1">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick();
            dismiss(toast.id);
          }}
          className="shrink-0 font-semibold underline underline-offset-2"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          dismiss(toast.id);
        }}
        aria-label={t('common.close')}
        className="grid size-6 shrink-0 place-items-center rounded-pill opacity-70 transition-opacity hover:opacity-100"
      >
        <svg viewBox="0 0 24 24" aria-hidden className="size-4">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * The single toast host. Mounted once, near the root.
 *
 * Two live regions rather than one: errors are `assertive` because they usually
 * mean the user's action did not happen, everything else is `polite` so a
 * success confirmation does not interrupt whatever is being read.
 */
export function ToastHost() {
  const toasts = useToastStore((state) => state.toasts);

  const assertive = toasts.filter((item) => item.tone === 'error');
  const polite = toasts.filter((item) => item.tone !== 'error');

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--tabbar-height)+var(--safe-bottom)+1rem)] z-50 mx-auto flex w-full max-w-[560px] flex-col gap-2 px-4">
      <div aria-live="assertive" aria-atomic="false" className="flex flex-col gap-2">
        {assertive.map((item) => (
          <ToastItem key={item.id} toast={item} />
        ))}
      </div>
      <div aria-live="polite" aria-atomic="false" className="flex flex-col gap-2">
        {polite.map((item) => (
          <ToastItem key={item.id} toast={item} />
        ))}
      </div>
    </div>
  );
}
